import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatches } from 'react-router';
import { Outlet, useLocation, useRouteLoaderData } from 'react-router-dom';

import { AppRouteKey } from '~/core/router/route_types';
import { AppRoutes } from '~/core/router/routes';
import { OrganizationMetadataModel } from '~/datasources/auth_datasource/models/organization_metadata_model';
import { BaseSidebarConfig } from '~/datasources/sidebar_datasource/models/sidebar_config';

import { GlobalLoadingScreen } from '../components/GlobalLoadingScreen';
import { ToastQueueProvider } from '../components/toast_queue_provider';
import { Sidebar } from '../sidebar/sidebar';
import { TopBar } from '../topbar/topbar';

interface AppLayoutWrapperProps {
  children: ReactNode;
}

// Authentication routes that should not have the sidebar
const authRoutes = [
  AppRoutes[AppRouteKey.LOGIN].path,
  AppRoutes[AppRouteKey.SIGNUP].path,
  AppRoutes[AppRouteKey.VERIFY_EMAIL].path,
  AppRoutes[AppRouteKey.VERIFY_INVITE].path,
  AppRoutes[AppRouteKey.NO_ACCESS].path,
];

export function AppLayoutWrapper({ children }: AppLayoutWrapperProps) {
  const location = useLocation();
  const { t } = useTranslation(['auth', 'common']);
  const currentPath = location.pathname;

  const loaderData = useRouteLoaderData('root') as {
    sidebarConfig: BaseSidebarConfig;
    organizations: OrganizationMetadataModel[];
    currentOrganization: OrganizationMetadataModel | undefined;
  };

  const matches = useMatches();
  const currentRouteLoaderData = matches[matches.length - 1]?.data ?? {};
  // A route opts out of the global TopBar via `export const handle = { hideTopBar: true }` -
  // shown by default, hidden only when a route explicitly asks for that (e.g. a page that
  // renders its own top bar, like the Global Plot Map).
  const currentRouteHandle = matches[matches.length - 1]?.handle as
    | { hideTopBar?: boolean }
    | undefined;
  const showTopBar = !currentRouteHandle?.hideTopBar;

  const handleOrganizationChange = (organization: OrganizationMetadataModel) => {
    console.log('Organization changed:', organization);
  };

  const isAuthRoute = authRoutes.some((route) => {
    return currentPath === route || currentPath.startsWith(`${route}/`);
  });

  // The content div below is keyed by path so the fade-in animation replays on
  // genuine page changes. Keying by the raw pathname also remounts the whole
  // subtree on param-only changes (e.g. switching plans within the Season Plan
  // page), discarding the dropdowns/tabs along with the content that actually
  // needs to update. Scoped to just these routes: key by the matched route's
  // stable id instead, so param changes within the same route re-render in
  // place while navigating to a genuinely different route still remounts (and
  // still replays the fade-in) as before. Every other route in the app keeps
  // the original pathname-keyed behavior unchanged.
  const isAgronomistSeasonPlanRoute = /^\/agronomist-season-plan\//.test(currentPath);
  const matchedRouteId = matches[matches.length - 1]?.id;
  const contentKey = isAgronomistSeasonPlanRoute && matchedRouteId ? matchedRouteId : currentPath;

  if (isAuthRoute) {
    return (
      <ToastQueueProvider>
        <div
          key={currentPath}
          className="min-h-screen animate-fade-in"
        >
          <Outlet />
        </div>
      </ToastQueueProvider>
    );
  }

  return (
    <ToastQueueProvider>
      <div className="flex h-screen flex-col">
        <Sidebar
          id={loaderData.sidebarConfig?.id ?? 'default'}
          metadata={loaderData.sidebarConfig?.metadata ?? { version: '1.0', theme: 'default' }}
          groups={loaderData.sidebarConfig?.groups ?? []}
          organizations={loaderData.organizations}
          currentOrganization={loaderData.currentOrganization}
          onOrganizationChange={handleOrganizationChange}
        >
          <div className="flex min-h-full flex-col">
            {showTopBar && (
              <TopBar
                currentPath={currentPath}
                loaderData={currentRouteLoaderData}
              />
            )}
            <div className="relative flex flex-1 flex-col">
              <GlobalLoadingScreen />
              <div className="flex w-full flex-1 flex-col">
                <div className="flex flex-1 flex-col">
                  <div
                    key={contentKey}
                    className="flex-1 animate-fade-in"
                  >
                    {children}
                  </div>
                  <footer className="w-full border-t border-neutral-200 py-2 text-center text-xs text-neutral-500">
                    © {new Date().getFullYear()} {t('rightsReserved')} • v{loaderData.version}
                  </footer>
                </div>
              </div>
            </div>
          </div>
        </Sidebar>
      </div>
    </ToastQueueProvider>
  );
}
